import { useState, type ComponentType, type ReactNode } from "react"
import { Eye, EyeOff, Lock } from "lucide-react"

/** Éléments partagés par l'écran de connexion et les écrans « mot de passe oublié ». */

export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div
      className="rounded-2xl p-6"
      style={{
        background: "rgba(12,20,35,0.75)",
        border: "1px solid rgba(30,41,59,0.55)",
        backdropFilter: "blur(12px)",
      }}
    >
      {children}
    </div>
  )
}

export function AuthHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="mb-5">
      <h2 className="text-[20px] font-bold text-white">{title}</h2>
      {subtitle && <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">{subtitle}</p>}
    </div>
  )
}

export function AuthButton({
  children,
  disabled,
  type = "button",
  onClick,
}: {
  children: ReactNode
  disabled?: boolean
  type?: "button" | "submit"
  onClick?: () => void
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-3.5 rounded-xl text-[15px] font-bold text-white flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-[0.98] mt-2 ${
        disabled ? "opacity-50" : ""
      }`}
      style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)" }}
    >
      {children}
    </button>
  )
}

export function Spinner() {
  return <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
}

interface AuthInputProps {
  icon: ComponentType<{ className?: string }>
  type: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  autoComplete?: string
  required?: boolean
  rightSlot?: ReactNode
}

export function AuthInput({
  icon: Icon,
  type,
  placeholder,
  value,
  onChange,
  autoComplete,
  required,
  rightSlot,
}: AuthInputProps) {
  const [focused, setFocused] = useState(false)
  return (
    <div
      className="flex items-center gap-3 px-4 h-[48px] rounded-xl transition-all"
      style={{
        background: focused ? "rgba(6,182,212,0.06)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${focused ? "rgba(6,182,212,0.35)" : "rgba(30,41,59,0.55)"}`,
      }}
    >
      <Icon className={`w-[18px] h-[18px] shrink-0 transition-colors ${focused ? "text-cyan-400" : "text-gray-500"}`} />
      <input
        type={type}
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete={autoComplete}
        required={required}
        className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-gray-600"
        style={{ caretColor: "#06b6d4" }}
      />
      {rightSlot}
    </div>
  )
}

/** Champ mot de passe avec l'œil pour l'afficher (même bouton que l'inscription). */
export function PasswordInput({
  placeholder,
  value,
  onChange,
  autoComplete,
  visible,
  onToggleVisible,
}: {
  placeholder: string
  value: string
  onChange: (v: string) => void
  autoComplete: string
  visible: boolean
  onToggleVisible: () => void
}) {
  return (
    <AuthInput
      icon={Lock}
      type={visible ? "text" : "password"}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      required
      rightSlot={
        <button
          type="button"
          onClick={onToggleVisible}
          className="p-1 cursor-pointer text-gray-500 hover:text-gray-300 transition-colors"
          tabIndex={-1}
          aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      }
    />
  )
}

/** Petite ligne d'aide ou d'erreur sous un champ. */
const HINT_TONE = { muted: "text-gray-500", warn: "text-orange-400", error: "text-red-400" } as const

export function FieldHint({ tone, children }: { tone: keyof typeof HINT_TONE; children: ReactNode }) {
  return <p className={`text-[11px] mt-1.5 ml-1 ${HINT_TONE[tone]}`}>{children}</p>
}
